// Vendor
import { Component, OnInit, LOCALE_ID, Inject } from "@angular/core";
import { Router, NavigationExtras } from "@angular/router";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { timer } from "rxjs";
import { debounce, tap } from "rxjs/operators";

// App
import { OrderService } from "../../services/order/order.service";
import { VALID_POSTCODE_REGEXP_BE } from "../../shared/validators/postcode.validator";

import { PATH_STEP_1 } from "../../app-routing.constants";

@Component({
    selector: "ig-step3",
    templateUrl: "./step3.component.html",
    styleUrls: ["./step3.component.scss"],
})
export class Step3Component implements OnInit {

    public privacyStatementUrl!: string;

    public submitted = false;
    public isSubmitting: boolean;
    public showContactGegevens = false;
    public form = new FormGroup(
        {
            givenName: new FormControl(
                this.orderService.order.givenName,
                [
                    Validators.required,
                ],
            ),
            surName: new FormControl(
                this.orderService.order.surName,
                [
                    Validators.required,
                ],
            ),
            postcode: new FormControl(
                this.orderService.order.postcode,
                [
                    Validators.required,
                    Validators.minLength(4),
                    Validators.pattern(VALID_POSTCODE_REGEXP_BE),
                ],
            ),
            number: new FormControl(
                this.orderService.order.number,
                [
                    Validators.required,
                ],
            ),
            addition: new FormControl(
                this.orderService.order.addition,
                [],
            ),
            street: new FormControl(
                this.orderService.order.street,
                [
                    Validators.required,
                ],
            ),
            city: new FormControl(
                this.orderService.order.city,
                [
                    Validators.required,
                ],
            ),
            firstPhoneNumber: new FormControl(
                this.orderService.order.firstPhoneNumber,
                [
                    Validators.required,
                    Validators.minLength(9),
                    Validators.maxLength(10),
                    Validators.pattern(/^0/),
                ],
            ),
            emailAddress: new FormControl(
                this.orderService.order.emailAddress,
                [
                    Validators.required,
                    Validators.email,
                ],
            ),
        },
    );

    constructor(
        public router: Router,
        public orderService: OrderService,

        @Inject(LOCALE_ID)
        private readonly locale: "nl" | "fr",
    ) {

        switch (locale) {
            case "nl":
                this.privacyStatementUrl = "https://www.pluimers.be/privacyverklaring/";
                break;
            case "fr":
                this.privacyStatementUrl = "https://www.pluimers.be/fr/clause-de-sauvegarde/";
                break;
        }
    }

    ngOnInit() {
        this.orderService.updateTotal();

        this.form.valueChanges
            .pipe(tap(() => this.submitted = false))
            .pipe(tap((values) => this.orderService.saveValues(values)))
            .pipe(debounce(() => timer(1e3)))
            .subscribe(() => {
                this.orderService.commitOrderToStorage();
                this.orderService.updateTotal();
            });

        this.formatPostcode();
    }

    /**
     * Safely gets errors from a control
     */
    public errors(controlName: string) {
        const { errors } = this.form.controls[controlName];

        return errors != null
            ? errors
            : {};
    }

    /**
     * Watches the input for postcode and reformats the input
     *
     * Makes sure that only uppercase characters can be entered and the format
     * uses no spaces and the length never exceeds 6 characters.
     *
     * The value changes are applied without emitting a change event to prevent
     * inifinite loops.
     */
    public formatPostcode() {
        const postcodeCtrl = this.form.controls["postcode"];

        const onChange = (val: string) => {
            const newVal = String(val || "")
                .toLocaleUpperCase()
                .replace(/[^A-Za-z0-9]/g, "")
                .substr(0, 6);

            postcodeCtrl.patchValue(newVal, {
                emitEvent: false,
            });
        };

        postcodeCtrl.valueChanges.subscribe(onChange);

        // Patch any pre-existing data on the model for sanity
        onChange(postcodeCtrl.value);
    }

    public onPrevButtonClick($event: Event) {
        const commands = [];
        const navigationExtras: NavigationExtras = {
            replaceUrl: true,
            queryParamsHandling: "merge",
        };

        commands.push(PATH_STEP_1);

        this.router.navigate(commands, navigationExtras);
    }

    public onNextButtonClick($event: Event) {

        if (this.isSubmitting) {
            $event.preventDefault();
            return;
        }

        // Set form as submitted to trigger showing of validation messages
        this.submitted = true;

        // Don't do anything if the form is not valid
        if (this.form.invalid) {
            $event.preventDefault();
            return;
        }

        this.isSubmitting = true;

        this.orderService
            .sendIt()
            .subscribe(
                res => this.onSuccessResponse(res),
                res => this.onErrorResponse(res),
            );
    }

    /**
     * Succesful order request response handler
     *
     * Takes the user to the next step with a thank you message.
     *
     * @param res Response of the successful request.
     */
    public onSuccessResponse(res) {
        this.orderService.order.step3Completed = true;
        switch (this.orderService.order.locale) {
            case "fr": {
                window.top.location.href = "https://www.gamma.be/fr/infos-consommateur/service-pose/confirmation";
                break;
            }
            case "nl": {
                window.top.location.href = "https://www.gamma.be/nl/klanteninformatie/montageservice/bevestiging";
                break;
            }
        }

    }

    /**
     * Failed order request response handler
     *
     * Shows a plain old alert and dumps the actual error to the console.
     *
     * @param res Error response of the failed order request.
     * @todo Make sure this client-side error is reported to Sentry somehow.
     */
    public onErrorResponse(res) {
        console.error({ res });

        const message = [
            "Er ging iets fout tijdens het verwerken van je order.",
            "Probeer het later nog een keer.",
        ].join(" ");

        window.alert(message);

        this.isSubmitting = false;
    }
}
